import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/api/client";

/** Tenor-backed GIF picker.
 *
 *  Mirrors the EmojiPicker's positioning contract (open / onClose /
 *  align) so the composer can drop it in next to the existing emoji
 *  popover. Two modes:
 *   - Empty query → /api/messaging/gifs/featured (trending)
 *   - Non-empty query → /api/messaging/gifs/search (debounced)
 *
 *  The grid is masonry-lite: a 2-column CSS columns layout that lets
 *  variable-height GIFs flow without us pre-computing layout. Click a
 *  thumbnail → ``onPick`` fires with the full-size GIF data; the
 *  parent owns "what to do with it" (we always send-on-pick rather
 *  than queue-then-Send, which matches Slack/Discord and avoids a
 *  weird double-action UX).
 */

interface GifItem {
  id: string;
  title: string;
  preview_url: string;
  src_url: string;
  width: number;
  height: number;
}

interface GifFeed {
  items: GifItem[];
  next: string | null;
}

interface PickPayload {
  url: string;
  width: number;
  height: number;
}

const SEARCH_DEBOUNCE_MS = 250;

export function GifPicker({
  open,
  onClose,
  onPick,
  align = "right",
}: {
  open: boolean;
  onClose: () => void;
  onPick: (gif: PickPayload) => void;
  align?: "left" | "right";
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<GifItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounced fetch. We re-issue on every keystroke but cancel the
  // pending timer so the network only fires after the user pauses.
  // ``cancelled`` flag protects against an in-flight response landing
  // after a newer query was kicked off.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fire = async () => {
      setLoading(true);
      setError(null);
      try {
        const q = query.trim();
        const path = q
          ? `/api/messaging/gifs/search?q=${encodeURIComponent(q)}`
          : "/api/messaging/gifs/featured";
        const r = await api<GifFeed>(path);
        if (cancelled) return;
        setItems(r.items);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 503) {
          setError("GIFs aren't enabled on this server.");
        } else {
          setError("Couldn't load GIFs. Try again.");
        }
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = window.setTimeout(fire, query ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, open]);

  // Outside-click + Esc to close. Standard popover wiring; mirrors
  // EmojiPicker so the two read the same to a power user.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (containerRef.current && t && !containerRef.current.contains(t)) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  // ``fixed`` (instead of ``absolute``) lifts the popover out of the
  // chat body's ``overflow-hidden`` scroll container, which otherwise
  // clips the left edge once the popover extends past the GIF
  // button's column. The drawer's transform makes the containing
  // block the aside itself, so ``right-3`` is "12px from the drawer's
  // right edge" — same visual anchor the user expects, without the
  // clipping. ``max-w`` keeps it inside narrow viewports too.
  // ``align`` is unused now (the popover's right edge is always
  // pinned to the drawer's right edge regardless of which button
  // opened it) — kept on the prop signature so callers don't have to
  // change.
  void align;
  return (
    <div
      ref={containerRef}
      className="fixed right-3 bottom-[68px] w-[320px] max-w-[calc(100vw-1.5rem)] max-h-[360px] rounded-lg bg-ink-900 border border-white/10 shadow-2xl z-[1200] overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-white/5">
        <input
          type="search"
          autoFocus
          placeholder="Search GIFs…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="input w-full text-sm h-8 [&::-webkit-search-cancel-button]:appearance-none"
        />
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {error ? (
          <div className="p-4 text-center text-[12px] text-rose-300">
            {error}
          </div>
        ) : items === null || (loading && items.length === 0) ? (
          <div className="p-4 text-center text-[12px] text-slate-500">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center text-[12px] text-slate-500">
            No GIFs match.
          </div>
        ) : (
          <div className="columns-2 gap-1.5 [column-fill:_balance]">
            {items.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() =>
                  onPick({
                    url: g.src_url,
                    width: g.width,
                    height: g.height,
                  })
                }
                title={g.title || "Send GIF"}
                className="block w-full mb-1.5 break-inside-avoid rounded overflow-hidden border border-white/10 hover:border-accent-500/40 transition-colors"
              >
                <img
                  src={g.preview_url}
                  alt={g.title || ""}
                  loading="lazy"
                  className="block w-full h-auto"
                />
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Giphy's brand guidelines require an attribution line on any
          surface that displays their results — the badge can be plain
          text as long as "GIPHY" is visibly credited. */}
      <div className="px-2 py-1 border-t border-white/5 text-[10px] text-slate-600 text-right">
        Powered by GIPHY
      </div>
    </div>
  );
}
